import React from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import logoImg from '../../assets/img/header/logo.svg';
import rookieIcon from '../../assets/img/2 MAIN SCREENS EN/Profile/Vector.png';
import copy from '../../features/home/homeCopy';
import { EggWalletChip, LangSwitch, AvatarButton, ProfileImage, getEggBalance, getStreakCount } from '../ui/GaPrimitives';
import { profileDisplayName } from '../../utils/profileForm';

const CutCornerButton = ({ text, onClick, primary = false }) => {
    return (
        <Box
            onClick={onClick}
            sx={{
                width: '100%',
                height: '56px',
                bgcolor: primary ? '#B8EE3C' : '#15170F',
                border: primary ? 'none' : '1px solid #262B1C',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 0 100%)',
                mb: 2,
                transition: 'opacity 0.2s',
                WebkitTapHighlightColor: 'transparent',
                '&:active': {
                    opacity: 0.7
                }
            }}
        >
            <Typography sx={{
                fontFamily: "'Rajdhani', sans-serif",
                fontWeight: 700,
                fontSize: 16,
                letterSpacing: '1px',
                color: primary ? '#0D0F0A' : '#9AA88A',
                textTransform: 'uppercase'
            }}>
                {text}
            </Typography>
        </Box>
    );
};

const formatPhone = (phone) => {
    if (!phone) return "";
    const digits = String(phone).replace(/\D/g, "");
    let local = digits;
    if (local.startsWith("92") && local.length > 10) local = `0${local.slice(2)}`;
    if (local.length < 10) return String(phone);
    return `${local.slice(0, 4)} •••• ${local.slice(-3)} • Jazz`;
};

const ProfileMenu = ({ onClose, lang, switchLang, profile }) => {
    const navigate = useNavigate();
    const t = copy[lang]?.profile || copy.en.profile;
    const skillLevel = (sessionStorage.getItem("skillLevel") || "rookie").toUpperCase();
    const eggs = getEggBalance(profile);
    const streak = getStreakCount(profile);

    return (
        <Box sx={{
            width: '100%',
            height: '100%',
            bgcolor: '#0D0F0A',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
                display: 'none',
            },
            scrollbarWidth: 'none',
        }}>
            {/* Header (Mirroring LayoutMobile) */}
            <Box sx={{
                height: '60px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 12px',
                borderBottom: '1px solid #262B1C',
                flexShrink: 0
            }}>
                <Box
                    onClick={() => { navigate('/'); onClose(); }}
                    sx={{
                        width: 107,
                        height: 43,
                        p: '6px 8px',
                        boxSizing: 'border-box',
                        bgcolor: '#B8EE3C',
                        cursor: 'pointer',
                    }}
                >
                    <Box
                        component="img"
                        src={logoImg}
                        alt="GamezAcademy"
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            filter: 'brightness(0)',
                        }}
                    />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', ml: 'auto', mr: '8px' }}>
                    <EggWalletChip count={eggs} />
                    <LangSwitch lang={lang} switchLang={switchLang} />
                </Box>
                <AvatarButton src={profile?.profileImage} onClick={onClose} size={34} />
            </Box>

            <Box sx={{ p: '24px 20px', flex: 1 }}>
                
                {/* Current Level Box */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: '#15170F',
                    border: '1px solid #262B1C',
                    p: '16px 20px',
                    mb: 2,
                    clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 0 100%)'
                }}>
                    <Typography sx={{
                        fontFamily: "'Rajdhani', sans-serif",
                        fontWeight: 700,
                        fontSize: 14,
                        letterSpacing: '2px',
                        color: '#6B7A5D',
                        textTransform: 'uppercase'
                    }}>{t.currentLevel}</Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box component="img" src={rookieIcon} alt="Rookie Badge" sx={{ width: 16, height: 16, objectFit: 'contain' }} />
                        <Typography sx={{
                            fontFamily: "'Rajdhani', sans-serif",
                            fontWeight: 700,
                            fontSize: 18,
                            color: '#FFFFFF',
                            textTransform: 'uppercase',
                            letterSpacing: '1px'
                        }}>{skillLevel}</Typography>
                    </Box>
                </Box>

                {/* Stats Row */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <Box sx={{
                        flex: 1,
                        bgcolor: '#15170F',
                        border: '1px solid #262B1C',
                        p: '20px 0',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 0 100%)'
                    }}>
                        <Typography sx={{
                            fontFamily: "'Rajdhani', sans-serif",
                            fontWeight: 700,
                            fontSize: 28,
                            color: '#B8EE3C',
                            lineHeight: 1,
                            mb: 0.5
                        }}>{eggs}</Typography>
                        <Typography sx={{
                            fontFamily: "'Rajdhani', sans-serif",
                            fontWeight: 700,
                            fontSize: 12,
                            letterSpacing: '1px',
                            color: '#6B7A5D',
                            textTransform: 'uppercase'
                        }}>{t.eggs}</Typography>
                    </Box>

                    <Box sx={{
                        flex: 1,
                        bgcolor: '#15170F',
                        border: '1px solid #262B1C',
                        p: '20px 0',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 0 100%)'
                    }}>
                        <Typography sx={{
                            fontFamily: "'Rajdhani', sans-serif",
                            fontWeight: 700,
                            fontSize: 28,
                            color: '#B8EE3C',
                            lineHeight: 1,
                            mb: 0.5
                        }}>{streak}</Typography>
                        <Typography sx={{
                            fontFamily: "'Rajdhani', sans-serif",
                            fontWeight: 700,
                            fontSize: 12,
                            letterSpacing: '1px',
                            color: '#6B7A5D',
                            textTransform: 'uppercase'
                        }}>{t.dayStreak}</Typography>
                    </Box>
                </Box>

                {/* Profile Info Box */}
                <Box sx={{
                    bgcolor: '#15170F',
                    border: '1px solid #262B1C',
                    p: '32px 20px',
                    mb: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 0 100%)'
                }}>
                    <Box sx={{ width: 80, height: 80, mb: 2, borderRadius: '50%', overflow: 'hidden', border: '3px solid #B8EE3C', bgcolor: '#262B1C' }}>
                        <ProfileImage src={profile?.profileImage} alt="Profile" />
                    </Box>
                    <Typography sx={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: 700,
                        fontSize: 22,
                        color: '#FFFFFF',
                        mb: 0.5
                    }}>{profileDisplayName(profile)}</Typography>
                    <Typography sx={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: 400,
                        fontSize: 14,
                        color: '#9AA88A',
                    }}>{formatPhone(profile?.phone)}</Typography>
                </Box>

                {/* Actions */}
                <CutCornerButton 
                    text={t.edit} 
                    primary={true} 
                    onClick={() => { navigate('/profile', { state: { fromProfileMenu: true } }); onClose(); }} 
                />
                <CutCornerButton 
                    text={t.myRewards} 
                    onClick={() => { navigate('/rewards', { state: { fromProfileMenu: true } }); onClose(); }} 
                />
                <CutCornerButton 
                    text={t.intro} 
                    onClick={() => { navigate('/intro', { state: { fromProfileMenu: true } }); onClose(); }} 
                />
                <CutCornerButton 
                    text={t.terms} 
                    onClick={() => { navigate('/terms', { state: { fromProfileMenu: true } }); onClose(); }} 
                />

            </Box>
        </Box>
    );
};

export default ProfileMenu;
